import React from 'react';
import { FragranceNotes, ScentProfileEntry } from '../../types/fragrance';
import './ProductNotes.css';

// Re-export so existing imports of these types from ProductNotes still work
export type { FragranceNotes, ScentProfileEntry };

interface ProductNotesProps {
  /** Full top/heart/base pyramid — fragrances */
  notes?: FragranceNotes;
  /** Dot-bar scent intensity chart — fragrances */
  profile?: ScentProfileEntry[];
  /** Simple one-line scent description — body products */
  scent?: string;
  /** Texture description — body products */
  texture?: string;
}

const TOTAL_DOTS = 5;

const ProductNotes: React.FC<ProductNotesProps> = ({ notes, profile, scent, texture }) => (
  <div className="product-notes">
    {/* ── Simple scent (body products) ── */}
    {scent && (
      <div className="product-notes__simple-scent">
        <p className="product-notes__section-label">Scent</p>
        <p className="product-notes__scent-text">{scent}</p>
      </div>
    )}

    {/* ── Texture (body products) ── */}
    {texture && (
      <div className="product-notes__texture">
        <p className="product-notes__section-label">Texture</p>
        <p className="product-notes__scent-text">{texture}</p>
      </div>
    )}

    {/* ── Scent Profile dot bars (fragrances) ── */}
    {profile && profile.length > 0 && (
      <div className="product-notes__profile">
        <p className="product-notes__section-label">Scent Profile</p>
        <dl className="product-notes__profile-list">
          {profile.map(({ label, level }) => (
            <div key={label} className="product-notes__profile-row">
              <dt className="product-notes__profile-label">{label}</dt>
              <dd className="product-notes__profile-dots" aria-label={`${level} out of ${TOTAL_DOTS}`}>
                {Array.from({ length: TOTAL_DOTS }, (_, i) => {
                  const dotIndex = i + 1; // Dots are 1-indexed
                  const isFilled = dotIndex <= Math.floor(level);
                  const isHalf = dotIndex === Math.ceil(level) && level % 1 !== 0;
                  
                  return (
                    <span
                      key={i}
                      className={`product-notes__dot ${isFilled ? 'product-notes__dot--filled' : ''} ${isHalf ? 'product-notes__dot--half' : ''}`}
                      aria-hidden="true"
                    />
                  );
                })}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    )}

    {/* ── Notes pyramid (fragrances) ── */}
    {notes && (
      <div className="product-notes__pyramid">
        <p className="product-notes__section-label">Notes</p>
        <div className="product-notes__tiers">
          <div className="product-notes__tier-row">
            <span className="product-notes__tier-label">Top</span>
            <span className="product-notes__tier-value">{notes.top}</span>
          </div>
          <div className="product-notes__tier-row">
            <span className="product-notes__tier-label">Heart</span>
            <span className="product-notes__tier-value">{notes.heart}</span>
          </div>
          <div className="product-notes__tier-row">
            <span className="product-notes__tier-label">Base</span>
            <span className="product-notes__tier-value">{notes.base}</span>
          </div>
        </div>
      </div>
    )}
  </div>
);

export default ProductNotes;
